import { useAuth } from '@blockworks/session/context';
import { Button, cn, FlexBox, Modal, Text } from '@blockworks/ui';
import { Form } from '@blockworks/ui/forms/form';
import { toast } from '@blockworks/ui/toast';

import { SHARE_FEEDBACK_OPTIONS } from '@/api/slack/internal/internal-slack.model';
import { slackMutations } from '@/api/slack/mutations';

import { useQueryModal } from '../../utils';

import { DEFAULT_CATEGORY, shareFeedbackFormSchema } from './share-feedback-modal.model';
import { getLabel } from './share-feedback-modal.utils';

const ShareFeedbackModal = () => {
    const { closeModal, modalParams, getRouteWithoutModalParams } = useQueryModal();
    const { modalCategory: defaultCategory } = modalParams;
    const { user } = useAuth();
    const { control, watch, handleSubmit, formState, reset } = Form.use({
        resolver: Form.resolvers.yup(shareFeedbackFormSchema),
        defaultValues: {
            category: defaultCategory,
        },
    });
    const watchFeedbackCategory = watch('category');
    const shareFeedback = slackMutations.shareFeedback.use();
    const handleOnSubmit = handleSubmit(async ({ category, description }) => {
        try {
            await shareFeedback.mutateAsync({
                category: category,
                description: description,
                userEmail: user?.email ?? '',
                route: getRouteWithoutModalParams(),
            });
            reset({ category: DEFAULT_CATEGORY, description: '' });
            toast.success(`Your ${getLabel(category)} was submitted successfully.`, { duration: 5000 });
            closeModal();
        } catch (err) {
            console.error(err);
            toast.danger(`Something went wrong. Please try again.`, { duration: 5000 });
        }
    });

    return (
        <Modal.Content>
            <Modal.Header>
                <FlexBox col gap={0}>
                    <Modal.Title>Share Feedback</Modal.Title>
                    <Text size="sm" color="muted">
                        Your feedback helps us improve Research.
                    </Text>
                </FlexBox>
            </Modal.Header>
            <Form>
                <Modal.Body className={cn('!w-[95vw] !h-[75vh] sm:!h-auto sm:!w-[500px] gap-2')}>
                    <FlexBox gap={2}>
                        <Form.Select
                            label="Category"
                            name="category"
                            control={control}
                            options={SHARE_FEEDBACK_OPTIONS}
                            fullWidth
                        />
                    </FlexBox>
                    <Form.TextArea
                        rows={8}
                        label="Description"
                        name="description"
                        control={control}
                        placeholder={`Please describe the ${getLabel(watchFeedbackCategory)} in detail.`}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <FlexBox justifyContent="end" gap={2} w="full">
                        <Button onClick={closeModal} disabled={formState.isSubmitting}>
                            Cancel
                        </Button>
                        <Form.Submit
                            onClick={handleOnSubmit}
                            loading={formState.isSubmitting}
                            loadingText="Submitting..."
                        >
                            Submit Feedback
                        </Form.Submit>
                    </FlexBox>
                </Modal.Footer>
            </Form>
        </Modal.Content>
    );
};

export { ShareFeedbackModal };
